import { tracer } from "../datadog";
import { Survey, Block, Text, BooleanExpr, ClickQuery, Collection, ExpandedSurvey, Notification, Payment, Section, Subsurvey, User, Users, SurveyComponent, NumberWithUnit, Root, SubsurveyRef, SubsurveyPathRef, TailwindColor, RichText } from "../survey";
import * as uuid from 'uuid';
import { nonNullable } from "../typechecks";
const slugid = require('slugid');

type Field = string;
type Tag = string;
type Email = string;
type Channel = string;

export type SupportedIcon = 'Collection' | 'Dashboard' | 'Notification' | 'Payment' | 'Section' | 'Subsurvey' | 'Queue' | 'SupportDashboard' | 'CogIcon' | 'DocumentTextIcon' | 'WalletIcon' | 'DocumentCurrencyDollarIcon' | 'TableCellsIcon' | 'Experimental';

export type BlockEdits = {
  block_when: BooleanExpr,
  blocked_message: RichText,
  block_mode?: 'full' | 'read_only',
  block_users_tagged?: string[]
}

export type ReviewConfiguration = {
  pinnedApplicantInfo: string[],
  showV2ToUsers?: string[],
  applicationState?: {
    field: string,
    colors: {
      stateValue: string,
      color: 'blue' | 'green' | 'red' | 'yellow'
    }[]
  }
  dropdownActions?: {
    viewOrResendSubsurveys?: boolean,
    seePayments?: boolean,
  }
}

export type NavigationNode = {
  name: string | Text,
  href: string,
  icon?: SupportedIcon | any, // any because of heroicons
  subnodes?: NavigationNode[], 
  isFolder?: boolean,
  limitedToApplicants?: BooleanExpr,
  shouldFastForward?: boolean | undefined,
  // Used for section identification for sections with the same title
  sectionPath?: string,
  defaultToClosed?: boolean,
  bold?: boolean,
  color?: TailwindColor,
  blockFurtherEdits?: BlockEdits,
  subsurveyTitle?: Text,
  reviewConfiguration?: ReviewConfiguration,
}

export function collectNavigation(props: Collection | Section | Subsurvey, scope?: "Global" | "Applicant", applicantUid?: string, path?: string): NavigationNode {
  const appLinkPrefix = (applicantUid && applicantUid?.length > 22) ? '/ua/' : '/a/';

  return {
    name: props.kind === 'Section' ? props.title : (props.kind === 'Subsurvey' ? '/p/' + props.path : props.name),
    href: props.kind === 'Section' ? appLinkPrefix + applicantUid + '#' + props.title.en : (props.kind === 'Subsurvey' ? appLinkPrefix + applicantUid + '/' + props.path : ''),
    sectionPath: props.kind === 'Section' ? path : '',
    icon: props.kind,
    isFolder: props.kind === 'Collection',
    limitedToApplicants: props.kind === 'Collection' ? props.options?.limitedToApplicants : undefined,
    shouldFastForward: props.kind === 'Subsurvey' ? props.shouldFastForward : undefined,
    defaultToClosed: props.kind === 'Collection' ? props.options.defaultToClosed : undefined,
    bold: props.kind === 'Collection' ? props.options.bold : undefined,
    color: props.kind === 'Collection' ? props.options.color : undefined,
    blockFurtherEdits: props.kind === 'Subsurvey' ? props.blockFurtherEdits : undefined,
    subsurveyTitle: props.kind === 'Subsurvey' ? props.title : undefined,
    reviewConfiguration: props.kind === 'Subsurvey' ? props.review as ReviewConfiguration | undefined : undefined,
    subnodes: (props.kind === 'Subsurvey' ? props.sections : props.components).map((c) => {
      // We no longer show subsurvey sections w/in the navigator since subsurveys 
      // are progressively navigated
      if (c.kind === 'Subsurvey') {
        if (scope === 'Global' || c.hideFromNavigation) return null;
        return collectNavigation(c, scope, applicantUid);
      }
      if (c.kind === 'Collection' || c.kind === 'Section') {
        if (scope === "Global" && c.kind !== "Collection") {
          return null;
        }
        if (c.kind === 'Section' && props.kind === 'Subsurvey') {
          // For sections inside subsurveys, need to also add in the path for uniqueness
          return collectNavigation(c, scope, applicantUid, props.path)
        }
        return collectNavigation(c, scope, applicantUid);
      }

      if (scope === 'Applicant' && c.kind === 'Applicant Identities') {
        return {
          name: c.title,
          href: '/ad/' + applicantUid + '/' + c.path,
          icon: 'Section',
          isFolder: false,
        } satisfies NavigationNode;
      }

      if (scope === 'Applicant' && c.kind === 'Applicant Portal Page') {
        return {
          name: c.title,
          href: '/ao/' + applicantUid + '/' + c.path,
          icon: 'Dashboard',
          isFolder: false
        } satisfies NavigationNode;
      }

      // The remaining pieces are all global scoped things
      if (scope === 'Applicant') return null;

      if (c.kind === 'Dashboard') {
        return {
          name: c.title,
          icon: 'Dashboard',
          href: '/d/' + c.path,
        } satisfies NavigationNode;
      }
      return null;

    }).filter(nonNullable),
  }
}

export function filterNavigation(nav: NavigationNode, scope: 'Global' | 'Applicant') {
  return {
    ...nav,
    subnodes: nav.subnodes?.map((c: NavigationNode): NavigationNode | null => {
      if (c.isFolder) {
        return filterNavigation(c, scope);
      }
      if (c.icon === 'Section' || c.icon === 'Subsurvey') {
        return scope === 'Global' ? null : c;
      } else {
        return scope === 'Global' ? c : null;
      }
    }).filter(nonNullable),
  }
}

export function collectApplicantNavigation(
  props: Collection | Section | Subsurvey,
  loggedIn: boolean,
  subnodes: NavigationNode[] = []
): NavigationNode {

  for (const c of (props.kind === 'Subsurvey' ? props.sections : props.components)) {
    if (c.kind === 'Applicant Portal Page' && (
      c.showWhen === 'Always' || c.showWhen === 'LoggedIn' && loggedIn || c.showWhen === 'LoggedOut' && !loggedIn
    )) {
      subnodes.push({
        name: c.title,
        href: '/o/' + c.path,
        icon: 'Dashboard',
        isFolder: false,
      });
    }
    if (c.kind === 'Collection' || c.kind === 'Section') {
      collectApplicantNavigation(c, loggedIn, subnodes);
    }
  }

  return {
    name: props.kind === 'Section' ? props.title : (props.kind === 'Subsurvey' ? '/p/' + props.path : props.name),
    href: '',
    icon: props.kind,
    isFolder: props.kind === 'Collection',
    subnodes: subnodes
  }
}

export type PermissionScope = {
  applicantScope: BooleanExpr,
  tags: Tag[],
  channelScope?: Channel[],
  userId?: string,
} & FieldAndEditScope;

export type FieldAndEditScope = {
  fieldScope: Field[],
  copyEditScope: SubsurveyPathRef[],
}

export function EmailToUserId(email: string) {
  return slugid.encode(uuid.v5(email.trim().toLowerCase(), uuid.v5.URL));
}

export function collectScopeForTags(tags: Tag[], survey: Survey): FieldAndEditScope {
  const permissions = CollectFieldAndEditPermissions(survey);
  return {
    fieldScope: Object.keys(permissions.fieldScope).filter(field => {
      const fieldTags = permissions.fieldScope[field];
      return fieldTags.length === 0 || tags.some(tag => fieldTags.includes(tag));
    }),
    copyEditScope: Object.keys(permissions.copyEditScope).filter(subsurveyPath => {
      const subsurveyTags = permissions.copyEditScope[subsurveyPath];
      return subsurveyTags.length === 0 || tags.some(tag => subsurveyTags.includes(tag));
    }),
  }
}

export function CollectScopeForUser(user: string, survey: Survey, users?: Users): PermissionScope {
  const [usersTags, userFilters] = CollectUserTagsAndFilters(users || []);
  if (!usersTags[user]) {
    return {
      applicantScope: {
        kind: 'Never'
      },
      fieldScope: [],
      channelScope: [],
      copyEditScope: [],
      tags: []
    }
  }
  const tags = usersTags[user];
  const filters = userFilters[user];
  const permissions = CollectFieldAndEditPermissions(survey);

  return {
    applicantScope: {
      kind: 'And',
      clauses: filters
    },
    fieldScope: Object.keys(permissions.fieldScope).filter(field => {
      const fieldTags = permissions.fieldScope[field];
      return fieldTags.length === 0 || tags.some(tag => fieldTags.includes(tag));
    }),
    channelScope: [],
    tags: [],
    copyEditScope: Object.keys(permissions.copyEditScope).filter(subsurveyPath => {
      const subsurveyTags = permissions.copyEditScope[subsurveyPath];
      return subsurveyTags.length === 0 || tags.some(tag => subsurveyTags.includes(tag));
    }),
  }
}

export function CollectIndividualUsers(users: Users) {
  const toReturn: (User & { tags: Tag[], filters: BooleanExpr[] })[] = [];
  function traverse(users: Users, tags: Tag[], filters: BooleanExpr[]) {
    users.forEach(user => {
      if (user.kind === 'User') {
        toReturn.push({
          ...user,
          tags,
          filters
        })
      }
      if (user.kind === 'User Group') {
        traverse(user.members, 
          [...tags, ...user.tags], 
          [...filters, ...(user.limitedToApplicants ? [user.limitedToApplicants] : [])])
      }
    })
  };
  traverse(users, [], []);
  return toReturn;
}

export function CollectUserTagsAndFilters(users: Users): [Record<Email, Tag[]>, Record<Email, BooleanExpr[]>] {
  const userTags = {} as Record<Email, Tag[]>;
  const userFilters = {} as Record<Email, BooleanExpr[]>;
  function traverse(users: Users, tags: Tag[], filters: BooleanExpr[]) {
    users.forEach(user => {
      if (user.kind === 'User') {
        const uid = EmailToUserId(user.email);
        userTags[uid] = tags;
        userFilters[uid] = filters;
      }
      if (user.kind === 'User Group') {
        traverse(user.members, 
          [...tags, ...user.tags], 
          [...filters, ...(user.limitedToApplicants ? [user.limitedToApplicants] : [])])
      }
    })
  };
  traverse(users, [], []);
  return [userTags, userFilters];
}

export function CollectFieldAndEditPermissions(survey: Root): { fieldScope: Record<Field, Tag[]>, copyEditScope: Record<SubsurveyPathRef, Tag[]> } {
  const fieldPermissions = {} as Record<Field, Tag[]>;
  const copyEditPermissions = {} as Record<SubsurveyPathRef, Tag[]>;

  const hasTagAlready = {} as Record<Field, Record<Tag, true | undefined>>;

  const addTagsToField = (targetField: Field, tags: Tag[]) => {
    // Don't add if field is empty
    if (!targetField) return;

    // If a field appears in a collection with no tags, 
    // intuit that means everyone should have access to it.
    // Overwrite existing tags, and given the if else below, 
    // it will always stay true the fieldPermissions[targetField] = [];
    if (tags.length === 0) {
      fieldPermissions[targetField] = tags;
      return;
    }

    hasTagAlready[targetField] ||= {};
        
    if (!fieldPermissions[targetField] || fieldPermissions[targetField]?.some(d => d)) {
      // If no tag array has been set or if specific tags have been defined,
      // Go ahead and push the rest.
      fieldPermissions[targetField] ||= [];

      for (const tag of tags) {
        if (!hasTagAlready[targetField][tag]) {
          fieldPermissions[targetField].push(tag);
          // Setting it in the record avoids tedious .includes calls
          hasTagAlready[targetField][tag] = true;
        }
      }
    } 

  }

  function traverse(block: SurveyComponent, tags: Tag[]) {
    if ('targetField' in block && block.targetField) {
      if ('allowedUserTags' in block) {
        tags = tags.concat(block.allowedUserTags);
      }
      addTagsToField(block.targetField, tags);
    }

    // Special case Likert because it is kind of a shell around its subquestions; it does
    // not have a top-level target field, but its subquestions do
    if (block.kind === 'Likert' && Array.isArray(block.questions)) {
      block.questions.forEach(question => {
        addTagsToField(question.targetField, tags);
      })
    }

    // Special case NumberWithUnit because it does not have a top-level target field
    if (block.kind === 'NumberWithUnit') {
      addTagsToField(block.numberQuestion.targetField, tags);
    }

    // This will only be present in raw/non-expanded surveys
    if (block.kind == 'Templated Block') {
      block.components.flatMap((s) => traverse(s, tags));
    }

    if (block.kind == 'Subsurvey') {
      copyEditPermissions[block.path] = block?.copyEditing?.allowedUserTags ?? [];
      block.sections.flatMap((s) => traverse(s, tags));
    }

    if (block.kind == 'Section') {
      tags = tags.concat(block.limitedToTags ?? []);
      block.components.flatMap((s) => traverse(s, tags));
    }

    if (block.kind == 'Collection') {
      tags = tags.concat(block.options.limitedToTags ?? []);
      block.components.flatMap((s) => traverse(s, tags));
    }

    if (block.kind == 'Conditional Block') {
      block.components.flatMap((s) => traverse(s, tags));
      (block.otherwise || []).flatMap((s) => traverse(s, tags));
    }

    if (block.kind === 'Notification' || block.kind === 'InlineNotification') {
      addTagsToField(block.targetPrefix, tags);
    }

    if (block.kind === 'Payment' || block.kind === 'GiveCard Mailing' || block.kind === 'USBank Card Mailing') {
      addTagsToField(block.targetField, tags);
    }
  }
  ((Array.isArray(survey) ? survey : survey.survey) || []).flatMap((s) => traverse(s, []));

  return { fieldScope: fieldPermissions, copyEditScope: copyEditPermissions };
}

/**
 * Given a survey, creates a new survey that only contains the
 * components that are accessible to a user with the given tags.
 * 
 * @param survey
 * @param tags user tags for checking against limitedToTags
 * @returns pruned version of the same survey
 */
export const pruneForUserTags = (survey: Survey, tags: Tag[]): Survey => {
  return survey.reduce((prev, s) => {
    if (s.kind === 'Collection') {
      if (s.options.limitedToTags?.length && 
                !s.options.limitedToTags.some((t) => tags.includes(t))) {
        console.log("PRUNING - Collection", s.options.limitedToTags, tags, s.name)
        return prev;
      }
      s.components = pruneForUserTags(s.components, tags);
    }
    if (s.kind === 'Subsurvey') {
      s.sections = pruneForUserTags(s.sections, tags) as Subsurvey['sections'];
    }
    if (s.kind === 'Section') {
      if (s.limitedToTags?.length && !s.limitedToTags.some((t) => tags.includes(t))) {
        console.log("PRUNING - Section", s.limitedToTags, tags, s.title);
        return prev;
      }
    }
    prev.push(s);
    return prev;
  }, [] as Survey)
};


/*
export function FilterSurveyForUser(user: string, survey: ExpandedSurvey): ExpandedSurvey {
}
*/
